import { useEffect, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";
import { motion } from "motion/react";
import type { PlayerView } from "../api/types";
import type { Theme } from "../theme/pixel";
import { factionFor } from "../theme/factions";
import { E32 } from "../theme/endesga32";
import { ManaIcon } from "./ManaIcon";
import { Sigil } from "./Sigil";
import { zoneMoveTransition } from "../logic/motion";
import {
  deckKey,
  exileKey,
  graveKey,
  useBoardRefs,
  type BoardRefKey,
} from "../logic/boardRefs";

interface SeatProps {
  view: PlayerView;
  faction: string;
  targetable?: boolean;
  targetPicked?: boolean;
  onTargetClick?: () => void;
  onInvalidTargetClick?: () => void;
  onExpand: () => void;
  onOpenDeck?: () => void;
  onOpenGraveyard?: () => void;
  onOpenExile?: () => void;
  deckHighlighted?: boolean;
  graveyardHighlighted?: boolean;
  exileHighlighted?: boolean;
  /** Total life this seat has lost this turn — drives the "−N" badge
   *  beside the Life number, mirroring PlayerPanel. */
  lifeLossThisTurn?: number;
}

interface Props {
  opponent: SeatProps;
  you: SeatProps;
  theme: Theme;
  /** When true, the rail grows to its expanded width and shows the
   *  rendered children (typically the two full PlayerPanels stacked).
   *  When false, shows the compact in-rail layout (sigil/Life/mana/zones). */
  expanded: boolean;
  /** Slot for the expanded content — passed through so BattlePage can
   *  drop in its existing PlayerPanel components. */
  expandedContent: ReactNode;
  /** Tap outside the rail when expanded → collapse. */
  onCollapse: () => void;
}

const COLLAPSED_WIDTH = 80;
const EXPANDED_WIDTH = 200;

export function PlayerRail({
  opponent,
  you,
  theme,
  expanded,
  expandedContent,
  onCollapse,
}: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCollapse();
    };
    const onClick = (e: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (e.target instanceof Node && root.contains(e.target)) return;
      onCollapse();
    };
    window.addEventListener("keydown", onKey);
    // Defer click registration to the next tick. Otherwise the same
    // click that triggered the expansion is still bubbling through
    // document and would immediately close the rail — the seat the
    // user clicked is detached during the content swap, so
    // root.contains(target) returns false and onCollapse fires.
    const t = window.setTimeout(() => {
      document.addEventListener("click", onClick);
    }, 0);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onClick);
    };
  }, [expanded, onCollapse]);

  const rootStyle: CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    width: expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
    background: theme.panel,
    border: `1px solid ${theme.panelEdge}`,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    transition: "width 220ms cubic-bezier(.2,.9,.2,1)",
    zIndex: expanded ? 30 : 1,
  };

  return (
    <div ref={rootRef} style={rootStyle}>
      {expanded ? (
        expandedContent
      ) : (
        <>
          <RailSeat seat={opponent} side="opponent" theme={theme} />
          <div style={{ height: 1, background: theme.panelEdge, flexShrink: 0 }} />
          <RailSeat seat={you} side="you" theme={theme} />
        </>
      )}
    </div>
  );
}

function RailSeat({
  seat,
  side,
  theme,
}: {
  seat: SeatProps;
  side: "opponent" | "you";
  theme: Theme;
}) {
  const f = factionFor(seat.faction);
  const isYou = side === "you";
  const targetableBg = seat.targetPicked
    ? `${theme.accent}22`
    : seat.targetable
      ? `${E32.amber}22`
      : "transparent";

  const onSeatBackgroundClick = () => {
    if (seat.targetable && seat.onTargetClick) {
      seat.onTargetClick();
    } else if (seat.onInvalidTargetClick) {
      seat.onInvalidTargetClick();
    } else {
      seat.onExpand();
    }
  };

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        padding: "8px 6px",
        background: targetableBg,
        cursor: "pointer",
        overflow: "hidden",
      }}
      onClick={onSeatBackgroundClick}
      role="button"
    >
      <div
        style={{
          width: 36,
          height: 36,
          background: f.color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Sigil faction={f.key} size={26} color={f.ink} />
      </div>

      <div
        style={{
          width: "100%",
          background: theme.bgDeep,
          border: `1px solid ${theme.panelEdge}`,
          borderRadius: theme.radius.button,
          padding: "4px 6px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontFamily: theme.fontBody,
            fontSize: 12,
            letterSpacing: theme.letterSpacing,
            textTransform: theme.uppercase ? "uppercase" : "none",
            fontVariant: theme.uppercase ? "normal" : "small-caps",
            color: theme.inkFaint,
          }}
        >
          Life
        </span>
        <span
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 16,
            fontWeight: 700,
            color: seat.view.life <= 25 ? E32.rust : theme.ink,
            lineHeight: 1,
            position: "relative",
          }}
        >
          {seat.view.life}
          {seat.lifeLossThisTurn != null && seat.lifeLossThisTurn > 0 && (
            <div
              // Same visual contract as PlayerPanel's badge: rust pill,
              // dark border. Anchored to the top-right of the life
              // number so it sits where the per-creature damage marker
              // would in the field.
              style={{
                position: "absolute",
                top: -8,
                right: -10,
                padding: "1px 4px",
                background: E32.rust,
                color: E32.white,
                fontFamily: theme.fontDisplay,
                fontSize: 8,
                border: `2px solid ${theme.bgDeep}`,
                lineHeight: 1,
              }}
            >
              −{seat.lifeLossThisTurn}
            </div>
          )}
        </span>
      </div>

      <div
        style={{
          width: "100%",
          background: theme.bgDeep,
          border: `1px solid ${theme.panelEdge}`,
          borderRadius: theme.radius.button,
          padding: "4px 6px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontFamily: theme.fontBody,
            fontSize: 12,
            letterSpacing: theme.letterSpacing,
            textTransform: theme.uppercase ? "uppercase" : "none",
            fontVariant: theme.uppercase ? "normal" : "small-caps",
            color: theme.inkFaint,
          }}
        >
          Mana
        </span>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 3,
          }}
        >
          {Array.from({ length: 10 }).map((_, i) => (
            <ManaIcon
              key={i}
              filled={i < seat.view.mana}
              exists={i < seat.view.max_mana}
              theme={theme}
              faction={f}
            />
          ))}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: "100%",
          flexShrink: 0,
        }}
        // Zone squares are buttons of their own — clicks shouldn't
        // bubble up to the seat-level expand/target click.
        onClick={(e) => e.stopPropagation()}
      >
        <ZoneSquare
          label="Deck"
          count={seat.view.deck_size}
          theme={theme}
          onClick={seat.onOpenDeck}
          highlighted={seat.deckHighlighted}
          anchorKey={deckKey(seat.view.player_id)}
          ghostEids={seat.view.deck?.map((c) => c.entity_id)}
        />
        <ZoneSquare
          label="Hand"
          count={isYou ? (seat.view.hand?.length ?? seat.view.hand_size) : seat.view.hand_size}
          theme={theme}
        />
        <ZoneSquare
          label="Grave"
          count={seat.view.graveyard_size}
          theme={theme}
          onClick={seat.onOpenGraveyard}
          highlighted={seat.graveyardHighlighted}
          anchorKey={graveKey(seat.view.player_id)}
          ghostEids={seat.view.graveyard?.map((c) => c.entity_id)}
        />
        <ZoneSquare
          label="Exile"
          count={seat.view.exile_size}
          theme={theme}
          onClick={seat.onOpenExile}
          highlighted={seat.exileHighlighted}
          anchorKey={exileKey(seat.view.player_id)}
          ghostEids={seat.view.exile?.map((c) => c.entity_id)}
        />
      </div>
    </div>
  );
}

function ZoneSquare({
  label,
  count,
  theme,
  onClick,
  highlighted,
  anchorKey,
  ghostEids,
}: {
  label: string;
  count: number;
  theme: Theme;
  onClick?: () => void;
  highlighted?: boolean;
  /** Register this square as a board-refs anchor for the pile. */
  anchorKey?: BoardRefKey;
  /** Invisible per-eid placeholders carrying Motion's `layoutId:
   *  card-<eid>` so the pile rect acts as the source / destination
   *  rect for each eid's shared-layout transition. */
  ghostEids?: ReadonlyArray<number>;
}) {
  const boardRefs = useBoardRefs();
  const interactive = onClick !== undefined;
  const borderColor = highlighted ? theme.accent : theme.panelEdge;
  return (
    <div
      ref={anchorKey ? (el) => boardRefs.register(anchorKey, el) : undefined}
      role={interactive ? "button" : undefined}
      onClick={interactive ? onClick : undefined}
      style={{
        background: theme.bgDeep,
        border: `1px solid ${borderColor}`,
        padding: "4px 2px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        cursor: interactive ? "pointer" : "default",
        userSelect: "none",
        position: "relative",
      }}
    >
      {ghostEids?.map((eid) => (
        <motion.span
          key={eid}
          layoutId={`card-${eid}`}
          transition={zoneMoveTransition()}
          aria-hidden="true"
          // 1px ghost at the pile's centre — see PlayerPanel comment.
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 1,
            height: 1,
            opacity: 0,
            pointerEvents: "none",
          }}
        />
      ))}
      <span
        style={{
          fontFamily: theme.fontBody,
          fontSize: 12,
          letterSpacing: theme.letterSpacing,
          textTransform: theme.uppercase ? "uppercase" : "none",
          fontVariant: theme.uppercase ? "normal" : "small-caps",
          color: theme.inkFaint,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 16,
          fontWeight: 700,
          color: theme.ink,
        }}
      >
        {count}
      </span>
    </div>
  );
}
